import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CheckCircle, AlertCircle, RefreshCw, ArrowRight, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setVerificationStatus('error');
      setMessage('No verification token provided');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      setIsLoading(true);
      setVerificationStatus('loading');

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/email-verification/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setVerificationStatus('success');
        setMessage(data.message || 'Email verified successfully!');
        toast.success('Email verified successfully!');
        
        // Refresh user data to update email_verified status
        if (fetchUser) {
          await fetchUser();
        }
      } else {
        const errorMessage = data.detail || data.message || 'Verification failed';
        if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
          setVerificationStatus('expired');
        } else {
          setVerificationStatus('error');
        }
        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus('error');
      setMessage('Network error occurred. Please check your connection and try again.');
      toast.error('Network error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (verificationStatus) {
      case 'loading':
        return <RefreshCw className="w-12 h-12 text-blue-600 animate-spin" />;
      case 'success':
        return <CheckCircle className="w-12 h-12 text-green-600" />;
      case 'error':
      case 'expired':
        return <AlertCircle className="w-12 h-12 text-red-600" />;
      default:
        return null;
    }
  };

  return (
    <div className="pt-32 min-h-screen bg-gradient-to-b from-blue-900/20 to-blue-800/30">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-6 mb-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Crown className="w-8 h-8 text-yellow-400" />
            <h1 className="text-3xl font-bold">World Underwater Macro Awards 2026</h1>
          </div>
          <div className="text-center">
            <span className="bg-yellow-400/20 text-yellow-200 border border-yellow-400/30 px-3 py-1 rounded-full text-sm">
              Email Verification
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                {getStatusIcon()}
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {verificationStatus === 'loading' && 'Verifying Email...'}
                {verificationStatus === 'success' && 'Email Verified!'}
                {verificationStatus === 'error' && 'Verification Failed'}
                {verificationStatus === 'expired' && 'Link Expired'}
              </h2>
              <p className="text-gray-600">{message}</p>
            </div>

            <div className="space-y-4">
              {verificationStatus === 'success' && (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-700 text-sm">
                      Your email has been successfully verified! Please login to complete your profile and start submitting photos.
                    </p>
                  </div>
                  
                  <div className="flex flex-col gap-3">
                    <Button 
                      onClick={() => navigate('/login')}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Login Now
                    </Button>
                  </div>
                </div>
              )}

              {(verificationStatus === 'error' || verificationStatus === 'expired') && (
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700 text-sm">
                      There was an issue verifying your email. Please try again or contact support if the problem persists.
                    </p>
                  </div>
                  
                  <Button 
                    onClick={verifyEmail}
                    disabled={isLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Try Again
                  </Button>
                </div>
              )}

              {verificationStatus === 'loading' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-700 text-sm text-center">
                    Please wait while we verify your email address...
                  </p>
                </div>
              )}
            </div>

            {/* Footer Links */}
            <div className="mt-8 text-center border-t pt-4">
              <Link 
                to="/" 
                className="text-blue-600 hover:text-blue-700 text-sm underline"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;